import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  UtensilsCrossed,
  Calendar,
  Heart,
  Check,
  Clock,
  Users
} from 'lucide-react'
import './App.css'

type MealType = 'breakfast' | 'lunch' | 'dinner'
type ViewType = 'today' | 'weekly' | 'preferences'
type CrowdLevel = 'low' | 'medium' | 'high'

interface MenuItem {
  name: string
  category: 'rice' | 'bread' | 'curry' | 'beverage' | 'dessert' | 'salad'
  liked?: boolean
}

interface Meal {
  type: MealType
  time: string
  items: MenuItem[]
  bookedSlot?: string
  availableSlots: { time: string; crowd: CrowdLevel }[]
}

const todayMenu: Meal[] = [
  {
    type: 'breakfast',
    time: '7:00 AM - 9:30 AM',
    items: [
      { name: 'Poha', category: 'rice' },
      { name: 'Idli Sambar', category: 'curry' },
      { name: 'Bread & Jam', category: 'bread' },
      { name: 'Chai', category: 'beverage' }
    ],
    availableSlots: [
      { time: '7:00-7:30', crowd: 'low' },
      { time: '7:30-8:00', crowd: 'medium' },
      { time: '8:00-8:30', crowd: 'high' },
      { time: '8:30-9:00', crowd: 'medium' },
      { time: '9:00-9:30', crowd: 'low' }
    ]
  },
  {
    type: 'lunch',
    time: '12:30 PM - 2:30 PM',
    items: [
      { name: 'Dal Rice', category: 'rice' },
      { name: 'Paneer Curry', category: 'curry' },
      { name: 'Roti', category: 'bread' },
      { name: 'Salad', category: 'salad' }
    ],
    availableSlots: [
      { time: '12:30-1:00', crowd: 'medium' },
      { time: '1:00-1:30', crowd: 'high' },
      { time: '1:30-2:00', crowd: 'high' },
      { time: '2:00-2:30', crowd: 'low' }
    ]
  },
  {
    type: 'dinner',
    time: '7:30 PM - 9:30 PM',
    items: [
      { name: 'Chole Bhature', category: 'curry' },
      { name: 'Jeera Rice', category: 'rice' },
      { name: 'Mix Veg', category: 'curry' },
      { name: 'Ice Cream', category: 'dessert' }
    ],
    availableSlots: [
      { time: '7:30-8:00', crowd: 'low' },
      { time: '8:00-8:30', crowd: 'high' },
      { time: '8:30-9:00', crowd: 'high' },
      { time: '9:00-9:30', crowd: 'medium' }
    ]
  }
]

const weeklyMenu = [
  { day: 'Mon', breakfast: 'Poha & Chai', lunch: 'Dal Rice & Paneer', dinner: 'Chole Bhature' },
  { day: 'Tue', breakfast: 'Upma & Coffee', lunch: 'Rajma Rice', dinner: 'Pasta & Salad' },
  { day: 'Wed', breakfast: 'Idli Sambar', lunch: 'Chole Rice', dinner: 'Fried Rice' },
  { day: 'Thu', breakfast: 'Paratha & Curd', lunch: 'Kadhi Rice', dinner: 'Pizza & Fries' },
  { day: 'Fri', breakfast: 'Bread Omelette', lunch: 'Biryani', dinner: 'Noodles' },
  { day: 'Sat', breakfast: 'Dosa & Chutney', lunch: 'Pav Bhaji', dinner: 'Dal Makhani' },
  { day: 'Sun', breakfast: 'Aloo Paratha', lunch: 'Special Thali', dinner: 'Burgers' }
]

function App() {
  const [currentView, setCurrentView] = useState<ViewType>('today')
  const [meals, setMeals] = useState<Meal[]>(todayMenu)
  const [selectedMeal, setSelectedMeal] = useState<MealType | null>(null)
  const [preferences, setPreferences] = useState<{[key: string]: boolean}>({})

  const getCrowdColor = (level: CrowdLevel) => {
    switch(level) {
      case 'low': return '#10b981'
      case 'medium': return '#f59e0b'
      case 'high': return '#ef4444'
    }
  }

  const getCrowdLabel = (level: CrowdLevel) => {
    switch(level) {
      case 'low': return 'Available'
      case 'medium': return 'Moderate'
      case 'high': return 'Crowded'
    }
  }

  const bookSlot = (mealType: MealType, slot: string) => {
    setMeals(meals.map(meal =>
      meal.type === mealType
        ? { ...meal, bookedSlot: slot }
        : meal
    ))
    setSelectedMeal(null)
  }

  const togglePreference = (itemName: string) => {
    setPreferences(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }))
  }

  return (
    <div className="app">
      {/* Header */}
      <motion.header
        className="header"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="header-content">
          <h1 className="logo">MessHub</h1>
          <div className="header-subtitle">Today • April 8</div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="main-content">
        <AnimatePresence mode="wait">
          {currentView === 'today' && (
            <motion.div
              key="today"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="view-container"
            >
              {meals.map((meal, index) => (
                <motion.div
                  key={meal.type}
                  className="meal-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                >
                  <div className="meal-header">
                    <div>
                      <h2 className="meal-title">{meal.type}</h2>
                      <p className="meal-time">
                        <Clock size={14} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                        {meal.time}
                      </p>
                    </div>
                    {meal.bookedSlot && (
                      <motion.div
                        className="booked-badge"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      >
                        <Check size={16} />
                        {meal.bookedSlot}
                      </motion.div>
                    )}
                  </div>

                  <div className="meal-items">
                    {meal.items.map((item) => (
                      <motion.div
                        key={item.name}
                        className="meal-item"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span className="item-name">{item.name}</span>
                      </motion.div>
                    ))}
                  </div>

                  <motion.button
                    className={`book-button ${meal.bookedSlot ? 'booked' : ''}`}
                    onClick={() => setSelectedMeal(meal.type)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Clock size={18} style={{ marginRight: '8px', display: 'inline' }} />
                    {meal.bookedSlot ? 'Change Slot' : 'Book Slot'}
                  </motion.button>
                </motion.div>
              ))}
            </motion.div>
          )}

          {currentView === 'weekly' && (
            <motion.div
              key="weekly"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="view-container"
            >
              <h2 className="section-title">This Week's Menu</h2>
              {weeklyMenu.map((day, index) => (
                <motion.div
                  key={day.day}
                  className="week-card"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <div className="week-day">{day.day}</div>
                  <div className="week-meals">
                    <div className="week-meal">
                      <span className="week-meal-label">B:</span>
                      <span>{day.breakfast}</span>
                    </div>
                    <div className="week-meal">
                      <span className="week-meal-label">L:</span>
                      <span>{day.lunch}</span>
                    </div>
                    <div className="week-meal">
                      <span className="week-meal-label">D:</span>
                      <span>{day.dinner}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {currentView === 'preferences' && (
            <motion.div
              key="preferences"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="view-container"
            >
              <h2 className="section-title">Food Preferences</h2>
              <p className="preferences-subtitle">
                Help us improve by marking your favorite dishes
              </p>

              <div className="preferences-grid">
                {meals.flatMap(meal => meal.items).map((item, index) => (
                  <motion.button
                    key={item.name}
                    className={`preference-item ${preferences[item.name] ? 'liked' : ''}`}
                    onClick={() => togglePreference(item.name)}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="preference-name">{item.name}</span>
                    <Heart
                      size={24}
                      fill={preferences[item.name] ? '#ff6b6b' : 'none'}
                      color={preferences[item.name] ? '#ff6b6b' : '#cbd5e1'}
                      style={{ transition: 'all 0.2s' }}
                    />
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        {[
          { id: 'today', label: 'Today', icon: UtensilsCrossed },
          { id: 'weekly', label: 'Weekly', icon: Calendar },
          { id: 'preferences', label: 'Favorites', icon: Heart }
        ].map((item) => {
          const Icon = item.icon
          return (
            <motion.button
              key={item.id}
              className={`nav-item ${currentView === item.id ? 'active' : ''}`}
              onClick={() => setCurrentView(item.id as ViewType)}
              whileTap={{ scale: 0.9 }}
            >
              <Icon
                size={24}
                strokeWidth={currentView === item.id ? 2.5 : 2}
                color={currentView === item.id ? '#ff6b6b' : '#94a3b8'}
              />
              <span className="nav-label">{item.label}</span>
            </motion.button>
          )
        })}
      </nav>

      {/* Slot Picker Modal */}
      <AnimatePresence>
        {selectedMeal && (
          <>
            <motion.div
              className="modal-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedMeal(null)}
            />
            <motion.div
              className="modal"
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.9 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              <h3 className="modal-title">Choose Your Slot</h3>
              <p className="modal-subtitle">
                {meals.find(m => m.type === selectedMeal)?.type}
              </p>

              <div className="slots-container">
                {meals.find(m => m.type === selectedMeal)?.availableSlots.map((slot, index) => (
                  <motion.button
                    key={slot.time}
                    className="slot-button"
                    onClick={() => bookSlot(selectedMeal, slot.time)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <span className="slot-time">
                      <Clock size={16} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                      {slot.time}
                    </span>
                    <span
                      className="slot-crowd"
                      style={{
                        backgroundColor: getCrowdColor(slot.crowd) + '20',
                        color: getCrowdColor(slot.crowd)
                      }}
                    >
                      <Users size={14} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                      {getCrowdLabel(slot.crowd)}
                    </span>
                  </motion.button>
                ))}
              </div>

              <motion.button
                className="modal-close"
                onClick={() => setSelectedMeal(null)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Cancel
              </motion.button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default App
